import { environment } from '@examdojo/core/environment';
import { decodeJwtPayload } from '@examdojo/core/util/decode-jwt-payload';

const TIPTAP_COLLAB_TOKEN = environment.tiptap.tiptap_collab_token || '';
const TIPTAP_CUSTOM_BACKEND = `${environment.examdojo.examdojoApiUrl}/tiptap`;

/**
 * Refresh the cached collab token well before it expires.
 * The backend issues tokens with a 24h lifetime; we refresh at 75% of that window
 * to avoid using a token that's about to expire.
 */
const COLLAB_TOKEN_REFRESH_RATIO = 0.75;

const COLLAB_TOKEN_STORAGE_KEY = 'collabToken';
const COLLAB_TOKEN_EXPIRES_STORAGE_KEY = 'collabTokenExpiresAt';
const COLLAB_TOKEN_USER_STORAGE_KEY = 'collabTokenUserId';

function readCachedToken(userId: string | null): { token: string; expiresAt: number } | null {
  try {
    const storedUserId = localStorage.getItem(COLLAB_TOKEN_USER_STORAGE_KEY);
    if (storedUserId !== userId) {
      clearCachedToken();
      return null;
    }

    const token = localStorage.getItem(COLLAB_TOKEN_STORAGE_KEY);
    const rawExpiresAt = localStorage.getItem(COLLAB_TOKEN_EXPIRES_STORAGE_KEY);
    if (token !== null && rawExpiresAt !== null) {
      return { token, expiresAt: Number(rawExpiresAt) };
    }
  } catch {
    // localStorage unavailable (e.g. SSR)
  }
  return null;
}

function writeCachedToken(token: string, expiresAt: number, userId: string | null): void {
  try {
    localStorage.setItem(COLLAB_TOKEN_STORAGE_KEY, token);
    localStorage.setItem(COLLAB_TOKEN_EXPIRES_STORAGE_KEY, String(expiresAt));
    if (userId) {
      localStorage.setItem(COLLAB_TOKEN_USER_STORAGE_KEY, userId);
    } else {
      localStorage.removeItem(COLLAB_TOKEN_USER_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable
  }
}

function clearCachedToken(): void {
  try {
    localStorage.removeItem(COLLAB_TOKEN_STORAGE_KEY);
    localStorage.removeItem(COLLAB_TOKEN_EXPIRES_STORAGE_KEY);
    localStorage.removeItem(COLLAB_TOKEN_USER_STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

export function invalidateCollabTokenCache(): void {
  inflight = null;
  clearCachedToken();
}

interface InflightRequest {
  userId: string | null;
  promise: Promise<string | null>;
}

let inflight: InflightRequest | null = null;

/**
 * Fetch collaboration JWT token from the API.
 * Caches the token in localStorage and refreshes proactively before expiry.
 * Deduplicates concurrent calls to avoid parallel requests.
 *
 * When `getAuthToken` returns a token, the backend `/collab-token`
 * endpoint is called. Failures (network, 4xx/5xx) are **not** caught
 * so the caller can treat them as errors.
 *
 * When `getAuthToken` returns `null` (no session), the hardcoded
 * `TIPTAP_COLLAB_TOKEN` is used as a fallback (e.g. content app).
 */
export const fetchCollabToken = async (getAuthToken: () => Promise<string | null>): Promise<string | null> => {
  const authToken = await getAuthToken();
  const userId = authToken ? extractSubFromAuthToken(authToken) : null;

  const nowSeconds = Math.floor(Date.now() / 1000);
  const cached = readCachedToken(userId);
  if (cached && nowSeconds < cached.expiresAt) {
    return cached.token;
  }

  if (inflight && inflight.userId === userId) {
    return inflight.promise;
  }

  const promise = doFetchCollabToken(authToken, userId).finally(() => {
    if (inflight?.promise === promise) {
      inflight = null;
    }
  });
  inflight = { userId, promise };

  return inflight.promise;
};

function extractSubFromAuthToken(authToken: string): string | null {
  const payload = decodeJwtPayload(authToken);
  return typeof payload?.['sub'] === 'string' ? payload['sub'] : null;
}

async function doFetchCollabToken(authToken: string | null, authUserId: string | null): Promise<string | null> {
  if (!authToken) {
    return TIPTAP_COLLAB_TOKEN || null;
  }

  const response = await fetch(`${TIPTAP_CUSTOM_BACKEND}/collab-token`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${authToken}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch collab token: ${response.status}`);
  }

  const data = await response.json();

  if (typeof data?.['token'] !== 'string') {
    throw new Error('Invalid collab token response: missing token field');
  }

  const token: string = data['token'];

  const payload = decodeJwtPayload(token);
  const exp = typeof payload?.['exp'] === 'number' ? payload['exp'] : null;

  if (exp) {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const lifetime = exp - nowSeconds;
    const expiresAt = nowSeconds + Math.floor(lifetime * COLLAB_TOKEN_REFRESH_RATIO);
    writeCachedToken(token, expiresAt, authUserId);
  }

  return token;
}
